import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Filter, X } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import AsyncCustomSelect from '../../../../ui/custom_react_select/async_select';
import { DatePicker } from '../../../../ui/input/date_picker/date_picker';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { IJobOpeningsDraftFilters } from '../interfaces';

export interface JobOpeningsFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  draftFilters: IJobOpeningsDraftFilters;
  onDraftChange: <K extends keyof IJobOpeningsDraftFilters>(
    key: K,
    value: IJobOpeningsDraftFilters[K],
  ) => void;
  onManagerChange: (opt: SelectOptions, isHiring: boolean) => void;
  onApply: () => void;
  onClear: () => void;
  isFilterDisabled: (filterKey: string) => boolean;
  defaultEmployeeUserOptions: SelectOptions[];
  loadOptionsDebounced: (
    inputValue: string,
    callback: (options: SelectOptions[]) => void,
  ) => void;
}

export const JobOpeningsFilterDrawer = ({
  isOpen,
  onClose,
  draftFilters,
  onDraftChange,
  onManagerChange,
  onApply,
  onClear,
  isFilterDisabled,
  defaultEmployeeUserOptions,
  loadOptionsDebounced,
}: JobOpeningsFilterDrawerProps) => {
  const { t } = useTranslation(NS.adminPages);

  const statuses: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.jobDescription), value: 'Job Description' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.jobPost), value: 'Job Post' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.shortlist), value: 'Shortlist' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.interview), value: 'Interview' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.decisionMaking), value: 'Decision Making' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.closed), value: 'Closed' },
  ];

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 540 },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: 'emerald.50',
              color: 'success.dark',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Filter size={18} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.title)}
          </Typography>
        </Stack>

        <IconButton size="small" onClick={onClose} aria-label="Close drawer">
          <X size={18} />
        </IconButton>
      </Box>

      {/* Form Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Time Range Section */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1.5,
                display: 'block',
              }}
            >
              {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.timeRange)}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.from)}
                </Typography>
                <DatePicker
                  asSingle
                  useRange={false}
                  startDate={draftFilters.dateRange?.startDate ?? null}
                  onChange={(val) =>
                    onDraftChange('dateRange', {
                      startDate: val?.startDate ?? null,
                      endDate: draftFilters.dateRange?.endDate ?? null,
                    })
                  }
                />
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.to)}
                </Typography>
                <DatePicker
                  asSingle
                  useRange={false}
                  startDate={draftFilters.dateRange?.endDate ?? null}
                  onChange={(val) =>
                    onDraftChange('dateRange', {
                      startDate: draftFilters.dateRange?.startDate ?? null,
                      endDate: val?.startDate ?? null,
                    })
                  }
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Job Details Section */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1.5,
                display: 'block',
              }}
            >
              Job Information
            </Typography>

            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.jobTitle)}
                value={draftFilters.jobTitle}
                onChange={(e) => onDraftChange('jobTitle', e.target.value)}
              />

              <TextField
                fullWidth
                size="small"
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.jobReference)}
                value={draftFilters.jobReferenceNumber}
                onChange={(e) => onDraftChange('jobReferenceNumber', e.target.value)}
              />

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.status)}
                </Typography>
                <AsyncCustomSelect
                  name="status"
                  defaultOptions={statuses}
                  onChange={(selectedOption: SelectOptions) => onDraftChange('status', selectedOption)}
                  loadOptions={(inputValue, callback) => callback(statuses)}
                  isMulti={false}
                  value={draftFilters.status}
                  placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.statusPlaceholder)}
                />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Stakeholders Section */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1.5,
                display: 'block',
              }}
            >
              Team & Recruiter
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.hiringManager)}
                </Typography>
                <AsyncCustomSelect
                  name="hiringManager"
                  defaultOptions={defaultEmployeeUserOptions}
                  onChange={(opt: SelectOptions) => onManagerChange(opt, true)}
                  loadOptions={(inputValue, callback) => loadOptionsDebounced(inputValue, callback)}
                  isMulti={false}
                  disabled={isFilterDisabled('Hiring Manager')}
                  value={draftFilters.hiringManager}
                />
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.internalRecruiter)}
                </Typography>
                <AsyncCustomSelect
                  name="internalRecruiter"
                  defaultOptions={defaultEmployeeUserOptions}
                  onChange={(opt: SelectOptions) => onManagerChange(opt, false)}
                  loadOptions={(inputValue, callback) => loadOptionsDebounced(inputValue, callback)}
                  isMulti={false}
                  disabled={isFilterDisabled('Internal Recruiter')}
                  value={draftFilters.internalRecruiter}
                />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Tags Section */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1.5,
                display: 'block',
              }}
            >
              {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.tags)}
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.tagsPlaceholder)}
              value={draftFilters.tags.join(', ')}
              onChange={(e) => {
                const tagsArray = e.target.value
                  .split(',')
                  .map((t) => t.trim())
                  .filter((t) => t.length > 0);
                onDraftChange('tags', tagsArray);
              }}
              helperText="Separate tags with commas"
            />
          </Box>
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.cancel)}
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="text"
            color="inherit"
            onClick={onClear}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.reset)}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={onApply}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              bgcolor: 'emerald.600',
              backgroundImage: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              '&:hover': {
                bgcolor: 'emerald.700',
              },
            }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.applyFilters)}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
